import React, { useState } from 'react';

const SubscriptionPricingModal = ({ isOpen, onClose, userEmail, userId, apiBaseUrl }) => {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('annual'); // Default to annual

  if (!isOpen) return null;

  const handleCheckout = async (planId) => {
    setCheckoutLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          user_email: userEmail,
          plan_id: planId,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        alert('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold text-slate-800">Choose Your Plan</h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Monthly Plan */}
            <div 
              className={`border-2 rounded-xl p-6 cursor-pointer transition-colors ${
                selectedPlan === 'monthly' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-orange-300'
              }`}
              onClick={() => setSelectedPlan('monthly')}
            >
              <div className="text-sm font-semibold text-orange-600 mb-2">MONTHLY</div>
              <div className="text-4xl font-bold text-slate-800 mb-2">
                $49<span className="text-lg text-slate-500">/mo</span>
              </div>
              <div className="text-slate-600 mb-4">Billed monthly</div>
              <div className="text-sm text-slate-500 mb-4">Includes 1,000 saved posts/month</div>
              
              <button
                onClick={(e) => { e.stopPropagation(); handleCheckout('pro_monthly'); }}
                disabled={checkoutLoading}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {checkoutLoading ? 'Loading...' : 'Choose Monthly'}
              </button>
            </div>
            
            {/* Annual Plan */}
            <div 
              className={`border-2 rounded-xl p-6 relative cursor-pointer transition-colors ${
                selectedPlan === 'annual' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-orange-300'
              }`}
              onClick={() => setSelectedPlan('annual')}
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                SAVE $120/YEAR
              </div>
              <div className="text-sm font-semibold text-orange-600 mb-2">ANNUAL</div>
              <div className="text-4xl font-bold text-slate-800 mb-2">
                $39<span className="text-lg text-slate-500">/mo</span>
              </div>
              <div className="text-slate-600 mb-4">$468/year billed annually</div>
              <div className="text-sm text-slate-500 mb-4">Includes 1,000 saved posts/month</div>
              
              <button
                onClick={(e) => { e.stopPropagation(); handleCheckout('pro_annual'); }}
                disabled={checkoutLoading}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {checkoutLoading ? 'Loading...' : 'Choose Annual'}
              </button>
              
              <ul className="mt-6 space-y-3">
                <li className="flex items-center text-green-600 font-medium">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save $120 vs monthly
                </li>
              </ul>
            </div>
          </div>
          
          {/* Cancel Note */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">Cancel anytime.</p>
          </div>
          
          {/* Enterprise Note */}
          <div className="mt-4 text-center">
            <p className="text-sm text-slate-500">
              Need more than 1,000 posts/month? <a href="mailto:support@trytickler.com?subject=Enterprise%20Plan%20Inquiry" className="text-orange-600 underline font-medium">Contact us</a> for Enterprise plans.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPricingModal;
